import { Component, ViewChild, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';

import { AuthService } from '../../services/auth.service';
import { AuthShellComponent } from '../auth-shell/auth-shell.component';
import { CountryPhoneInputComponent } from '../country-phone-input/country-phone-input.component';
import { OtpInputComponent } from '../otp-input/otp-input.component';
import { ROUTES } from '../../../core/constants/routes.constants';
import { environment } from '../../../../environments/environment';
import { ResetIdentifierMethod } from '../../interfaces/auth.type';

const OTP_LENGTH = 6;

@Component({
  selector: 'app-forgot-password',
  imports: [FormsModule, RouterLink, AuthShellComponent, CountryPhoneInputComponent, OtpInputComponent],
  templateUrl: './forgot-password.component.html',
  styleUrl: './forgot-password.component.scss'
})
export class ForgotPasswordComponent {
  private readonly authService = inject(AuthService);

  @ViewChild(OtpInputComponent) private otpInput?: OtpInputComponent;

  protected readonly routes = ROUTES;
  protected readonly isDummyMode = environment.dummy;

  protected readonly method = signal<ResetIdentifierMethod>('phone');

  protected readonly country = signal('India');
  protected readonly phone = signal('');
  protected readonly email = signal('');

  protected readonly otpCode = signal('');
  protected readonly otpSent = signal(false);
  protected readonly otpSending = signal(false);

  protected readonly newPassword = signal('');
  protected readonly confirmPassword = signal('');
  protected readonly newPasswordVisible = signal(false);
  protected readonly confirmPasswordVisible = signal(false);
  protected readonly resetting = signal(false);
  protected readonly resetComplete = signal(false);

  protected readonly errorMessage = signal('');

  protected readonly hasMinLength = computed(() => this.newPassword().length >= 8);
  protected readonly hasNumber = computed(() => /\d/.test(this.newPassword()));
  protected readonly hasSpecialChar = computed(() => /[^A-Za-z0-9]/.test(this.newPassword()));

  protected readonly identifierValid = computed(() =>
    this.method() === 'phone' ? !!this.phone() : !!this.email()
  );

  protected readonly submitLabel = computed(() => {
    if (!this.otpSent()) return this.otpSending() ? 'Sending OTP…' : 'Send OTP';
    return this.resetting() ? 'Resetting…' : 'Reset Password';
  });

  protected readonly submitDisabled = computed(() => {
    if (!this.otpSent()) return this.otpSending() || !this.identifierValid();
    return (
      this.resetting() ||
      this.otpCode().length !== OTP_LENGTH ||
      !this.hasMinLength() ||
      !this.hasNumber() ||
      !this.hasSpecialChar() ||
      this.newPassword() !== this.confirmPassword()
    );
  });

  setMethod(method: ResetIdentifierMethod): void {
    this.method.set(method);
    this.errorMessage.set('');
    this.otpSent.set(false);
    this.otpCode.set('');
  }

  toggleNewPasswordVisibility(): void {
    this.newPasswordVisible.update((visible) => !visible);
  }

  toggleConfirmPasswordVisibility(): void {
    this.confirmPasswordVisible.update((visible) => !visible);
  }

  handleSubmit(): void {
    if (!this.otpSent()) {
      this.sendOtp();
    } else {
      this.resetPassword();
    }
  }

  sendOtp(): void {
    this.errorMessage.set('');
    this.otpSending.set(true);

    this.authService
      .sendPasswordResetOtp({
        method: this.method(),
        phoneNumber: this.method() === 'phone' ? this.phone() : undefined,
        email: this.method() === 'email' ? this.email() : undefined
      })
      .subscribe({
        next: () => {
          this.otpSending.set(false);
          this.otpSent.set(true);
          setTimeout(() => this.otpInput?.start());
        },
        error: () => {
          this.otpSending.set(false);
          this.errorMessage.set('Could not send OTP. Please try again.');
        }
      });
  }

  resetPassword(): void {
    this.errorMessage.set('');

    if (this.newPassword() !== this.confirmPassword()) {
      this.errorMessage.set('Passwords do not match.');
      return;
    }

    this.resetting.set(true);

    this.authService
      .resetPassword({
        method: this.method(),
        phoneNumber: this.method() === 'phone' ? this.phone() : undefined,
        email: this.method() === 'email' ? this.email() : undefined,
        code: this.otpCode(),
        newPassword: this.newPassword()
      })
      .subscribe({
        next: () => {
          this.resetting.set(false);
          this.resetComplete.set(true);
        },
        error: (error: Error) => {
          this.resetting.set(false);
          this.errorMessage.set(error.message || 'Could not reset your password. Please try again.');
        }
      });
  }
}
